import { CommonModule, NgFor } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import { OrganizationRider, OrganizationRiderDetail, OrganizationService, OrganizationServiceLevel, Rider } from '@uzurv/clara';
import { PermissionsConstants, StringValidators, UzurvDirectivesModule, UzurvPipesModule } from '@uzurv/core';
import { MaterialModule } from 'projects/uzurv/src/app/material.module';
import { OrganizationRiderState } from 'projects/uzurv/src/store/organization-rider/organization-rider.reducer';
import { map } from 'rxjs';
import * as OrganizationRiderActions from 'projects/uzurv/src/store/organization-rider/organization-rider.actions';
import { OrganizationSelectModule } from '../../organization-select';
import { AuthDataService } from '@uzurv/http';

export interface OrganizationRiderDialogData {
  uzurvRider: Rider;
  organizations: Array<any>;
  organizationRiderDetails: Array<OrganizationRiderDetail>;
}

@Component({
  selector: 'app-add-organization-rider-dialog',
  templateUrl: './add-organization-rider-dialog.component.html',
  styleUrls: ['./add-organization-rider-dialog.component.scss'],
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    NgFor,
    ReactiveFormsModule,
    FormsModule,
    UzurvDirectivesModule,
    UzurvPipesModule,
    OrganizationSelectModule
  ]
})
export class AddOrganizationRiderDialogComponent implements OnInit {
  addOrganizationRiderForm: FormGroup;
  organizationServiceLevels: Array<OrganizationServiceLevel>;
  hasDuplicateOrganizations: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrganizationRiderDialogData,
    private authDataService: AuthDataService,
    private dialogRef: MatDialogRef<AddOrganizationRiderDialogComponent>,
    private formBuilder: FormBuilder,
    private organizationService: OrganizationService,
    private store$: Store<State<OrganizationRiderState>>
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    const riderHydrationData: Rider = this.data.uzurvRider;

    if (!this.authDataService.hasPermission({ all: [PermissionsConstants.RIDER_READ] }) && this.data.organizationRiderDetails?.length > 0) {
      riderHydrationData.firstName = this.data.organizationRiderDetails[0].firstName;
      riderHydrationData.lastName = this.data.organizationRiderDetails[0].lastName;
      riderHydrationData.emailAddress = this.data.organizationRiderDetails[0].emailAddress;
      riderHydrationData.phoneNumber = this.data.organizationRiderDetails[0].phoneNumber;
      riderHydrationData.dateOfBirth = this.data.organizationRiderDetails[0].dateOfBirth;
    }

    this.addOrganizationRiderForm = this.formBuilder.group({
      organization: [null, [Validators.required, this.uniqueOrganizations.bind(this)]],
      firstName: [riderHydrationData?.firstName, Validators.required],
      lastName: [riderHydrationData?.lastName, Validators.required],
      email: [riderHydrationData?.emailAddress, Validators.email],
      phoneNumber: [riderHydrationData?.phoneNumber],
      partnerIdentifier: [null, StringValidators.noSpecialCharacters()],
      dateOfBirth: [riderHydrationData?.dateOfBirth],
      riderServiceLevelsFormArray: this.formBuilder.array([])
    });
  }

  get riderServiceLevelsFormArray(): FormArray {
    return this.addOrganizationRiderForm?.get('riderServiceLevelsFormArray') as FormArray;
  }

  handleOrganizationSelected(event: any) {
    this.hasDuplicateOrganizations = this.data.organizationRiderDetails.some(
      organizationRiderDetail => organizationRiderDetail.organizationId === event.id
    );

    this.organizationService
      .listOrganizationServiceLevels(event.id)
      .pipe(
        map(osl => {
          this.organizationServiceLevels = osl;
          const filteredOrganizationServiceLevels = this.organizationServiceLevels.filter(osl => osl.optional);

          this.riderServiceLevelsFormArray.clear();
          if (filteredOrganizationServiceLevels.length > 0) {
            filteredOrganizationServiceLevels.forEach(organizationServiceLevel => {
              this.riderServiceLevelsFormArray.push(this.createServiceLevelFormGroup(organizationServiceLevel));
            });
          }
        })
      )
      .subscribe();
  }

  createServiceLevelFormGroup(organizationServiceLevel: OrganizationServiceLevel): FormGroup {
    return this.formBuilder.group({
      serviceLevel: [organizationServiceLevel.serviceLevel],
      enabled: [organizationServiceLevel.optional ? null : true]
    });
  }

  handleSubmission() {
    const rider = new Rider();

    Object.assign(rider, {
      firstName: this.addOrganizationRiderForm.get('firstName')?.value,
      lastName: this.addOrganizationRiderForm.get('lastName')?.value,
      emailAddress: this.addOrganizationRiderForm.get('email')?.value,
      phoneNumber: this.addOrganizationRiderForm.get('phoneNumber')?.value,
      dateOfBirth: this.addOrganizationRiderForm.get('dateOfBirth')?.value,
      id: this.data.uzurvRider.id
    });

    const organizationRider = new OrganizationRider();

    Object.assign(organizationRider, {
      firstName: this.addOrganizationRiderForm.get('firstName')?.value,
      lastName: this.addOrganizationRiderForm.get('lastName')?.value,
      emailAddress: this.addOrganizationRiderForm.get('email')?.value,
      phoneNumber: this.addOrganizationRiderForm.get('phoneNumber')?.value,
      dateOfBirth: this.addOrganizationRiderForm.get('dateOfBirth')?.value,
      organizationId: this.addOrganizationRiderForm.get('organization')?.value.id,
      partnerIdentifier: this.addOrganizationRiderForm.get('partnerIdentifier')?.value,
      rider: rider
    });

    this.store$.dispatch(
      OrganizationRiderActions.CreateOrganizationRider({
        payload: {
          organizationRider: organizationRider,
          organization: this.addOrganizationRiderForm.get('organization')?.value,
          organizationRiderServiceLevels: this.riderServiceLevelsFormArray.value,
          forNewItinerary: false,
          forAddingOrganizationRider: true
        }
      })
    );

    this.dialogRef.close();
  }

  changeServiceLevel(index: number, event: any) {
    const serviceLevelFormGroup = this.riderServiceLevelsFormArray.at(index) as FormGroup;
    serviceLevelFormGroup.patchValue({ enabled: event.value });
  }

  private uniqueOrganizations(): ValidationErrors | null {
    if (this.hasDuplicateOrganizations) {
      return { duplicateOrganizations: true };
    }

    return null;
  }
}
